import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from '@mui/material';
import { Button } from 'reactstrap';
import { FruitSizeMeasurement } from '../../../types/fruitsize';
import { formatDateYYYYMMDDHHMMSS, getNewRecordID } from '../../../helpers/utils';
import { Tree } from '../../../types/tree';
import { useSessionStore } from '../../../services/stores';
import { getFruitSizeStats } from '../../../helpers/stats';
import { SampleCountStats } from './SampleCountStats';
import { useSoundPlayer } from '../../../helpers/useSoundPlayer';

interface FruitSizeInputModalProps {
  open: boolean;
  onClose: () => void;
  onMeasurementsUpdate: (measurements: FruitSizeMeasurement[]) => void;
  existingMeasurements: FruitSizeMeasurement[];
  selectedTree: Tree | undefined;
  selectedFruitID?: string | undefined;
}

export const FruitSizeInputModal: React.FC<FruitSizeInputModalProps> = ({
  open,
  onClose,
  onMeasurementsUpdate,
  existingMeasurements,
  selectedTree,
  selectedFruitID = undefined
}) => {
  const [currentInput, setCurrentInput] = useState<string>('');
  const [lastKeyTime, setLastKeyTime] = useState<number>(0);
  const [measurements, setMeasurements] = useState<FruitSizeMeasurement[]>(existingMeasurements);
  const BATCH_TIMEOUT = 500;

  const farmID = useSessionStore().currentFarm.farmID;
  const playSound = useSoundPlayer({ format: 'wav', volume: 0.8 });



  // Update measurements when existingMeasurements changes
  useEffect(() => {
    setMeasurements(existingMeasurements);
  }, [existingMeasurements]);

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    const currentTime = Date.now();
    
    // Accept digits and decimal point
    if (!/^[\d.]$/.test(event.key)) {
      return;
    }

    // Prevent multiple decimal points
    if (event.key === '.' && currentInput.includes('.')) {
      return;
    }

    setLastKeyTime(currentTime);

    // If it's been longer than BATCH_TIMEOUT since the last key,
    // start a new measurement
    if (currentTime - lastKeyTime > BATCH_TIMEOUT && currentInput !== '') {
      const numericValue = parseFloat(currentInput);
      if (!isNaN(numericValue)) {
        const newMeasurement = {
          id: getNewRecordID(),
          farmID: farmID,
          treeID: selectedTree?.id,
          blockID: ((selectedTree?.id)? selectedTree?.blockID : undefined),
          varietyID: ((selectedTree?.varietyID)? selectedTree?.varietyID : undefined),
          fruitID: selectedFruitID,
          diameter_mm: numericValue,
          timestamp:formatDateYYYYMMDDHHMMSS(new Date()) || '',
          selected: false,
          status: 'new' as const,
        };
        const updatedMeasurements = [...measurements, newMeasurement];
        setMeasurements(updatedMeasurements);
        onMeasurementsUpdate(updatedMeasurements);
      }
      setCurrentInput(event.key);
    } else {
      setCurrentInput(prev => prev + event.key);
    }
  }, [currentInput, lastKeyTime, measurements, onMeasurementsUpdate]);

  useEffect(() => {
    if (open) {
      window.addEventListener('keypress', handleKeyPress);
      return () => {
        window.removeEventListener('keypress', handleKeyPress);
      };
    }
  }, [handleKeyPress, open]);

  // Handle final measurement after timeout
  useEffect(() => {
    if (currentInput === '' || !open) return;

    const timer = setTimeout(() => {
      const numericValue = parseFloat(currentInput);
      if (!isNaN(numericValue)) {
        const newMeasurement = {
          id: getNewRecordID(),
          diameter_mm: numericValue,
          farmID: farmID,
          treeID: selectedTree?.id,
          blockID: ((selectedTree?.id)? selectedTree?.blockID : undefined),
          varietyID: ((selectedTree?.varietyID)? selectedTree?.varietyID : undefined),
          fruitID: selectedFruitID,
          timestamp: formatDateYYYYMMDDHHMMSS(new Date()) || '',
          selected: false,
          status: 'new' as const,
        };
        const updatedMeasurements = [...measurements, newMeasurement];
        setMeasurements(updatedMeasurements);
        onMeasurementsUpdate(updatedMeasurements);
        setCurrentInput('');
        playSound('success');
      }
    }, BATCH_TIMEOUT);

    return () => clearTimeout(timer);
  }, [currentInput, open, measurements, onMeasurementsUpdate]);

  const handleClose = () => {
    setCurrentInput('');
    onClose();
  };

  const handleRemoveSelectedMeasurements = () => {
    const updatedMeasurements = measurements.map(measurement => ({
        ...measurement,
        status: (measurement.selected? 'deleted' as const : measurement.status),
        farmID: farmID,
        selected: false
      }));
      setMeasurements(updatedMeasurements);
      onMeasurementsUpdate(updatedMeasurements);

  };

  const handleMeasurementToggle = (measurementId: string) => {
    const updatedMeasurements = measurements.map(measurement => 
      measurement.id === measurementId 
        ? { ...measurement, selected: !measurement.selected }
        : measurement
    );
    setMeasurements(updatedMeasurements);
    onMeasurementsUpdate(updatedMeasurements);
  };


  const handleSelectAll = () => {
    const allSelected = measurements.every(m => m.selected);
    const updatedMeasurements = measurements.map(measurement => ({
      ...measurement,
      selected: !allSelected
    }));
    setMeasurements(updatedMeasurements);
    onMeasurementsUpdate(updatedMeasurements);
  };

  const selectedTreeMeasurements = measurements.filter(m => m.treeID === selectedTree?.id && m.status !== 'deleted'); 
  const allSelected = selectedTreeMeasurements.length > 0 && selectedTreeMeasurements.every(m => m.selected);
  const someAreSelected = selectedTreeMeasurements.some(m => m.selected) && !allSelected;

  const messagePrompt = currentInput? 
                        "Mesure en cours: " + currentInput + " mm" 
                        : 
                        "En attente de la prochaine mesure" ;

  const stats = getFruitSizeStats(selectedTreeMeasurements);
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        style: {
          width: '96%',
          height: '96%',
          maxWidth: 'none',
          maxHeight: 'none'
        }
      }}
    >
      <DialogContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
            <h6 style={{fontSize: '0.7rem', color: 'var(--bs-primary)'}} > {messagePrompt}</h6>
        </Box>

        <SampleCountStats 
          nSamples={selectedTreeMeasurements.length}
          n95={stats.n95}
          confidence={stats.confidence || 0}
        />

        <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead style={{ backgroundColor: 'lightgray' }}>
              <TableRow>
                <TableCell style={{ width: '15%', padding: '4px 2px' }}>#</TableCell>
                <TableCell style={{ width: '40%', padding: '1px 2px' }}>Calibre (mm)</TableCell>
                <TableCell style={{ width: '35%', padding: '1px 2px' }}>Heure</TableCell>
                <TableCell style={{ width: '10%', padding: '1px 2px' }}>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someAreSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...selectedTreeMeasurements]
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .map((measurement, index) => (
                <TableRow key={measurement.id}   >
                <TableCell style={{ width: '15%', padding: '4px 2px', textAlign:'center', color: 'lightgray' }}>{selectedTreeMeasurements.length - index}</TableCell>
                <TableCell style={{ width: '40%', padding: '1px 2px' }}>{measurement.diameter_mm.toFixed(2)}</TableCell>
                <TableCell style={{ width: '35%', padding: '1px 2px' }}>
                    {(new Date(measurement.timestamp)).toLocaleString('fr-FR', {  
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false
                    })}
                  </TableCell>
                  <TableCell style={{ width: '10%', padding: '1px 2px' }}>
                    <Checkbox
                      size="small"
                      checked={measurement.selected}
                      onChange={() => handleMeasurementToggle(measurement.id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      
      <DialogActions>
        <Button onClick={handleRemoveSelectedMeasurements} color="danger" disabled={!(someAreSelected || allSelected)} className="mr-2 px-3">
          Supprimer
        </Button>
        <Button onClick = {handleClose} color="primary" className="mr-2 px-3" >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 

/**
 *         <Button onClick={handleClose} variant="contained">

 */